/**
 * getPropsFromPromise
 *
 * Load data from a promise. It does not block render while it is fetching
 * the promise.
 *
 * `TOuter` is inferred from the factory's parameter annotation and becomes the public prop
 * type of the returned wrapper, so callers are checked against it. Annotate that parameter
 * (rather than leaving it `$TSFixMe`) to get that checking. A factory that ignores props
 * gives inference nothing to work with, so `TOuter` falls back to a permissive default and
 * the wrapper accepts arbitrary props, as it did before this module was typed.
 *
 * Note that `compose()` from recompose takes untyped `Function[]`, so a call site wrapped in
 * `compose<TInner, TOutter>(...)` takes its prop type from those explicit arguments and is not
 * checked against `TOuter`. Direct `getPropsFromPromise(fn)(Component)` calls are.
 *
 * 1. If you want to block rendering your component until the data is there, use this
 * in conjunction with `js/lib/waitFor`.
 *
 * module.exports = compose(
 *   getPropsFromPromise((props) => {
 *     return Q.all([
 *     	getCourseFromId(props.courseId),
 *     	getInstructorForCourse(props.courseId)
 *     ]).spread((course, instructor) => ({
 *       course: course
 *       instructor: instructor
 *     }))
 *   }),
 *   waitFor(props => !!course && !!instructor)
 * )(SomeComponent)
 *
 * 2. If you want to custom handle logic in your component while the data is loading,
 * use `js/lib/mapProps` to give yourself an `isLoaded` prop that your component can hook onto
 *
 * module.exports = compose(
 *   getPropsFromPromise((props) => {
 *     return Q.all([
 *     	getCourseFromId(props.courseId),
 *     	getInstructorForCourse(props.courseId)
 *     ]).spread((course, instructor) => ({
 *       course: course
 *       instructor: instructor
 *     }))
 *   }),
 *   mapProps(props => ({
 *     ...props,
 *     isLoaded: !!course && !!instructor
 *   }))
 * )(SomeComponent)*
 */
import * as React from 'react';

import isEqual from 'lodash/isEqual';
import without from 'lodash/without';

import hoistNonReactStatics from 'js/lib/hoistNonReactStatics';

// Callers pass Q promises as often as native ones. `done` is Q-only, and `catch` is absent from
// the structural `PromiseLike` the factory is constrained to, so both are probed before use -
// exactly as the original untyped implementation did.
type SettleablePromise = PromiseLike<unknown> & {
  catch?: (onRejected: (reason: unknown) => unknown) => unknown;
  done?: () => void;
};

export default function getPropsFromPromise<
  TOuter = Record<string, unknown>,
  TResult extends PromiseLike<unknown> = PromiseLike<unknown>,
>(promiseFactoryFn: (props: TOuter) => TResult) {
  return function <P extends Partial<TOuter & Awaited<TResult>>>(
    Component: React.ComponentType<P>
  ): React.ComponentClass<TOuter> {
    const componentName = Component.displayName || Component.name;

    class GetPropsFromPromiseWrapper extends React.Component<TOuter, Partial<Awaited<TResult>>> {
      static displayName = componentName + 'GetPropsFromPromiseWrapper';

      componentDidMount() {
        this.update(this.props);
      }

      UNSAFE_componentWillReceiveProps(nextProps: TOuter) {
        const currentProps = this.props as Record<string, unknown>;
        const incomingProps = nextProps as Record<string, unknown>;

        const allKeys = new Set([...Object.keys(incomingProps), ...Object.keys(currentProps)]);
        const uniqueKeys = [...allKeys];

        const filteredKeys = without(
          uniqueKeys,
          'children',
          'history',
          'location',
          'params',
          'route',
          'routeParams',
          'routes'
        );

        const refresh = filteredKeys.some((propKey) => !isEqual(currentProps[propKey], incomingProps[propKey]));

        if (refresh) {
          this.update(nextProps);
        }
      }

      update(props: TOuter) {
        const promise: SettleablePromise = promiseFactoryFn(props).then((data) => {
          this.setState(data as Awaited<TResult>);
        });

        if (promise.catch) {
          promise.catch((e) => {
            throw e;
          });
        }

        if (promise.done) {
          promise.done();
        }
      }

      render() {
        // The wrapped component's props are the outer props merged with whatever the promise
        // resolved to; `P` is only constrained to be a subset of that union, so the merge cannot
        // be proven to equal `P` structurally.
        const props = Object.assign({}, this.props, this.state) as unknown as P;

        return React.createElement(Component, props);
      }
    }

    hoistNonReactStatics(GetPropsFromPromiseWrapper, Component);

    return GetPropsFromPromiseWrapper;
  };
}
